import React from 'react';
import getConfig from 'next/config';

import Link from '~/src/components/link/Link';
import MainLogo from '~/src/components/svg/navigation/MainLogo';
import NavRadio from '~/src/components/svg/navigation/NavRadio';
import NavVideo from '~/src/components/svg/navigation/NavVideo';
import NavCockpit from '~/src/components/svg/navigation/NavCockpit';
import NavNews from '~/src/components/svg/navigation/NavNews';
import NavLifestyle from '~/src/components/svg/navigation/NavLifestyle';
import NavProfile from '~/src/components/svg/navigation/NavProfile';
import Layout from '~/src/components/layout/Layout';
import NavigationCategory from '~/src/components/navigation/category/NavigationCategory';
import NavigationSubcategory from '~/src/components/navigation/subcategory/NavigationSubcategory';
import Ad from '~/src/components/ad/Ad';
import UserProfileButton from '~/src/components/user/profile/button/UserProfileButton';

import {classNames} from '~/src/utilities/helpers/css';
import {SiteArea} from '~/src/utilities/constants/navigation';
import {getCategoryUrl, getPageUrl} from '~/src/utilities/helpers/link';
import {isEnabled} from '~/src/utilities/helpers/feature';
import {Feature} from '~/src/utilities/constants/feature';
import {getVideoFront} from '~/src/utilities/helpers/video';
import {getLifestyleFront} from '~/src/utilities/helpers/lifestyle';
import {withSettingsContext} from '~/src/components/context/SettingsContext';
import {withSitemapContext} from '~/src/components/context/SitemapContext';
import {withNavigationStateContext} from '~/src/components/context/NavigationStateContext';
import initPolyglot from '~/src/utilities/libs/polyglot';
import {createNavigationTracker} from '~/src/utilities/tracking/gtm';

import css from './Navigation.module.scss';
import i18nStrings from './Navigation.i18n.json';

const {publicRuntimeConfig: config} = getConfig();
const getTranslation = initPolyglot(i18nStrings);

class Navigation extends React.Component {
    navigationTop = React.createRef();

    videoFrontCategory = isEnabled(Feature.VIDEO) && getVideoFront(this.props.context.sitemap.all);
    lifestyleFrontCategory = isEnabled(Feature.LIFESTYLE) && getLifestyleFront(this.props.context.sitemap.all);

    loadRadioPlayer() {
        window.open(getCategoryUrl('radio'), 'targetWindow', 'width=330, height=567');
        return false;
    }

    shouldComponentUpdate(nextProps, nextState) {
        return (
            this.props.categoryId !== nextProps.categoryId ||
            this.props.pageKey !== nextProps.pageKey ||
            this.props.context.navigationState.isCollapsed ||
            nextProps.context.navigationState.isCollapsed
        );
    }

    render() {
        const {categoryId, isContentPage, showSubCategories, context} = this.props;
        const {siteArea, isFromLegacy, isWebView} = context.settings;
        const {isCollapsed} = context.navigationState;

        if (isWebView && (siteArea !== SiteArea.LIFESTYLE || isContentPage)) {
            return null;
        }

        const category = context.sitemap.getCategoryBy({categoryId});
        const categoryHasSiblings = category && context.sitemap.getCategoryBy({categoryId: category.parent});
        const categoryHasChildren = category && category.children && category.children.length;

        const className = classNames(css, 'navigation', siteArea, {isCollapsed});

        const frontUrl = isFromLegacy ? `https://www.20min.ch/${config.language === 'fr' ? 'ro' : ''}` : getCategoryUrl('');

        return (
            <React.Fragment>
                <nav className={className} data-testid="Navigation">
                    {!isWebView && (
                        <React.Fragment>
                            <div ref={this.navigationTop} className={css.navigationTop}>
                                <Layout>
                                    <div className={css.content}>
                                        <ul className={css.list}>
                                            <li
                                                className={classNames(css, 'item', 'main', siteArea, {isActive: siteArea === SiteArea.NEWS})}
                                                data-testid="MainLogo"
                                            >
                                                <Link href={frontUrl} className={classNames(css, 'link')} onClick={createNavigationTracker(1, 'logo')}>
                                                    <MainLogo siteArea={siteArea} />
                                                </Link>
                                            </li>

                                            <li className={classNames(css, 'item', 'news', {isActive: siteArea === SiteArea.NEWS})}>
                                                <Link
                                                    href={getCategoryUrl('')}
                                                    className={css.link}
                                                    onClick={createNavigationTracker(1, getTranslation('news'))}
                                                >
                                                    <NavNews
                                                        primaryClassName={css.primaryColor}
                                                        secondaryClassName={css.secondaryColor}
                                                        tertiaryClassName={css.tertiaryColor}
                                                    />
                                                    {getTranslation('news')}
                                                </Link>
                                            </li>

                                            {isEnabled(Feature.VIDEO) && this.videoFrontCategory && (
                                                <li className={classNames(css, 'item', 'video', {isActive: siteArea === SiteArea.VIDEO})}>
                                                    <Link
                                                        href={getCategoryUrl(this.videoFrontCategory.fullUrlPath)}
                                                        className={css.link}
                                                        onClick={createNavigationTracker(1, getTranslation('video'))}
                                                    >
                                                        <NavVideo
                                                            primaryClassName={css.primaryColor}
                                                            secondaryClassName={css.secondaryColor}
                                                            tertiaryClassName={css.tertiaryColor}
                                                        />
                                                        {getTranslation('video')}
                                                    </Link>
                                                </li>
                                            )}

                                            {isEnabled(Feature.RADIO) && (
                                                <li className={classNames(css, 'item', 'radio')}>
                                                    <button
                                                        className={css.link}
                                                        onClick={() => {
                                                            this.loadRadioPlayer();
                                                            createNavigationTracker(1, getTranslation('radio'))();
                                                        }}
                                                    >
                                                        <NavRadio />
                                                        {getTranslation('radio')}
                                                    </button>
                                                </li>
                                            )}

                                            {isEnabled(Feature.LIFESTYLE) && this.lifestyleFrontCategory && (
                                                <li className={classNames(css, 'item', 'lifestyle', {isActive: siteArea === SiteArea.LIFESTYLE})}>
                                                    <Link
                                                        href={getCategoryUrl(this.lifestyleFrontCategory.fullUrlPath)}
                                                        target="_blank"
                                                        className={css.link}
                                                        onClick={createNavigationTracker(1, getTranslation('lifestyle'))}
                                                    >
                                                        <NavLifestyle />
                                                        {getTranslation('lifestyle')}
                                                    </Link>
                                                </li>
                                            )}

                                            <li className={classNames(css, 'item', 'cockpit', {isActive: siteArea === SiteArea.COCKPIT})}>
                                                <Link
                                                    href={getPageUrl('cockpit')}
                                                    className={css.link}
                                                    onClick={createNavigationTracker(1, getTranslation('cockpit'))}
                                                >
                                                    <NavCockpit
                                                        primaryClassName={css.primaryColor}
                                                        secondaryClassName={css.secondaryColor}
                                                        tertiaryClassName={css.tertiaryColor}
                                                    />
                                                    {getTranslation('cockpit')}
                                                </Link>
                                            </li>

                                            <li className={classNames(css, 'item', 'profile', {isActive: siteArea === SiteArea.PROFILE})}>
                                                {isEnabled(Feature.LOGIN) && (
                                                    <UserProfileButton className={css.link} onProfile={createNavigationTracker(1, getTranslation('profile'))}>
                                                        <NavProfile
                                                            primaryClassName={css.primaryColor}
                                                            secondaryClassName={css.secondaryColor}
                                                            tertiaryClassName={css.tertiaryColor}
                                                        />
                                                    </UserProfileButton>
                                                )}
                                            </li>
                                        </ul>
                                    </div>
                                </Layout>
                            </div>
                        </React.Fragment>
                    )}

                    {category && <NavigationCategory key={`category_${siteArea}`} {...category} />}

                    {(!category || !context.sitemap.isFront(category)) && (
                        <Layout>
                            <Ad key={`inside_full_sticky_${this.props.pageKey}`} adUnitName="inside-full-sticky" />
                        </Layout>
                    )}
                </nav>

                {showSubCategories && (categoryHasChildren || categoryHasSiblings) && <NavigationSubcategory {...category} />}
            </React.Fragment>
        );
    }
}

export default withNavigationStateContext(withSitemapContext(withSettingsContext(Navigation)));
